import React, { useMemo, useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Modal, Space, Switch, Table, message, type InputRef, type TableColumnType, type TableColumnsType } from "antd";
import "./data_sales_order_invoice_return.css";
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { useNavigate } from "react-router-dom";
import useSWR, { useSWRConfig } from 'swr';
import axios from 'axios';
import { useAuth } from "../../context/AuthContext";


interface DataType {
    key: string;
    invoice_return_no: string;
    date: string;
    invoice_no: string;
    bill_to: string;
    total: number;
    status: string;
}

type DataIndex = keyof DataType;


const fetcher = (url: string) => axios.get(url).then(res => res.data);


const DataSalesOrderInvoiceReturn: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();
    const { userData } = useAuth();
    const { mutate } = useSWRConfig();
    const [showAllWarehouse, setShowAllWarehouse] = useState(false);
    const { data, error } = useSWR<DataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/sales-order-invoice-returns?all=${showAllWarehouse}`, fetcher);

    if (error) {
        console.error("Error fetching sales order invoice return data:", error);
    }

    const handleRowDoubleClick = async (record: DataType) => {
        const key = 'loading';
        message.loading({ content: 'Loading data...', key });
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sales-order-invoice-return/${record.invoice_return_no}`);
            if (response.data) {
                localStorage.setItem('selectedInvoiceReturnData', JSON.stringify(response.data));
                message.success({ content: 'Data loaded successfully!', key, duration: 2 });
                navigate('/dashboard/sales_order_invoice_return');
            } else {
                throw new Error("No data received");
            }
        } catch (err) {
            console.error("Failed to fetch invoice return details:", err);
            message.error({ content: 'Failed to load data. Please try again.', key, duration: 2 });
        }
    };

    const canApprove = useMemo(() => {
        return userData?.otoritas?.split(';').includes('03') ?? false;
    }, [userData]);

    const canShowAllWarehouse = useMemo(() => {
        return userData?.otoritas?.split(';').includes('77') ?? false;
    }, [userData]);

    const handleApprove = async (invoiceReturnNo: string) => {
        const key = 'checking-status';
        message.loading({ content: 'Memeriksa status sales order...', key });
        try {
            const checkRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/check-will-cancel-so/${invoiceReturnNo}`);
            message.destroy(key);
            const willCancel = checkRes.data.willCancel;
            const modalContent = willCancel
                ? "Sales Order akan di Cancel karena semua part di kembalikan !, apakah setuju? Proses ini tidak dapat di kembalikan."
                : `Apakah Anda yakin ingin menyetujui invoice return (${invoiceReturnNo})?`;
            Modal.confirm({
                title: willCancel ? 'Konfirmasi Pembatalan Sales Order' : 'Konfirmasi Approval',
                content: modalContent,
                okText: 'Ya, Setujui',
                cancelText: 'Tidak',
                onOk: async () => {
                    const approveKey = 'approving';
                    message.loading({ content: 'Memproses approval...', key: approveKey });
                    try {
                        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/approve-invoice-return/${invoiceReturnNo}`);
                        message.success({ content: 'Invoice return berhasil disetujui.', key: approveKey, duration: 2 });
                        mutate(`${process.env.REACT_APP_BACKEND_URL}/api/sales-order-invoice-returns`);
                    } catch (err: any) {
                        console.error("Gagal menyetujui invoice return:", err);
                        const errorMessage = err.response?.data?.message || 'Gagal menyetujui invoice return. Silakan coba lagi.';
                        message.error({ content: errorMessage, key: approveKey, duration: 3 });
                    }
                },
            });
        } catch (err) {
            message.destroy(key);
            console.error("Gagal memeriksa status cancel:", err);
            Modal.confirm({
                title: 'Konfirmasi Approval',
                content: `Apakah Anda yakin ingin menyetujui invoice return (${invoiceReturnNo})?`,
                okText: 'Ya, Setujui',
                cancelText: 'Tidak',
                onOk: async () => {
                    const approveKey = 'approving';
                    message.loading({ content: 'Memproses approval...', key: approveKey });
                    try {
                        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/approve-invoice-return/${invoiceReturnNo}`);
                        message.success({ content: 'Invoice return berhasil disetujui.', key: approveKey, duration: 2 });
                        mutate(`${process.env.REACT_APP_BACKEND_URL}/api/sales-order-invoice-returns`);
                    } catch (err: any) {
                        console.error("Gagal menyetujui invoice return:", err);
                        const errorMessage = err.response?.data?.message || 'Gagal menyetujui invoice return. Silakan coba lagi.';
                        message.error({ content: errorMessage, key: approveKey, duration: 3 });
                    }
                },
            });
        }
    };


    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.replace(/_/g, ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Invoice Return No',
            dataIndex: 'invoice_return_no',
            key: 'invoice_return_no',
            ...getColumnSearchProps('invoice_return_no'),
            sorter: (a, b) => a.invoice_return_no.localeCompare(b.invoice_return_no),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            ...getColumnSearchProps('date'),
            sorter: (a, b) => a.date.localeCompare(b.date),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Invoice No',
            dataIndex: 'invoice_no',
            key: 'invoice_no',
            ...getColumnSearchProps('invoice_no'),
            sorter: (a, b) => a.invoice_no.localeCompare(b.invoice_no),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Bill To',
            dataIndex: 'bill_to',
            key: 'bill_to',
            ...getColumnSearchProps('bill_to'),
            sorter: (a, b) => a.bill_to.localeCompare(b.bill_to),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            ...getColumnSearchProps('total'),
            sorter: (a, b) => a.total - b.total,
            sortDirections: ['descend', 'ascend'],
            render: (text: number) => Number(text).toLocaleString('id-ID'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...getColumnSearchProps('status'),
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="ilhr">
                <div className="gjs-grid-column" id="iob5">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Data Sales Order Invoice Return</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/sales_order_invoice_return",
                                title: "Sales Order Invoice Return",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="i6e2">
                <div className="gjs-grid-column" id="ig96">
                    {canShowAllWarehouse && (
                        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Switch checked={showAllWarehouse} onChange={setShowAllWarehouse} />
                            <span style={{ fontSize: '14px' }}>Show All Warehouse</span>
                        </div>
                    )}
                    <Table<DataType>
                        columns={columns}
                        dataSource={data}
                        loading={!data && !error}
                        onRow={(record) => ({
                            onDoubleClick: () => handleRowDoubleClick(record),
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataSalesOrderInvoiceReturn;